import React, { useState, useEffect, FunctionComponent } from 'react';
import {
  View,
  TabBar,
  Loading,
  Breadcrumbs,
  Link,
  Text,
} from '@upstox/upstox-ui';
import IPOHeader from './IPOHeader';
import IPOTimeLine from './IPOTimeLine';
import * as css from './IPODetails.style';

import useCurrentWidth from '../../hooks/useCurrentWidth';
import { observer } from 'mobx-react';
import { Ipo } from 'store/types';
import { IPO_STATUS } from '../../constants';
import { OverViewTab } from './OverviewTab';
import { IPODetailsCard } from './IPODetailsCard';

interface Props {
  queryParam: string;
  ipoDetails: Ipo;
  statusTab?: React.ReactNode;
  applyIpoButton?: React.ReactNode;
  applicationDeleted?: boolean;
  allowBottomPadding?: boolean;
  showFullCompanyDetails?: boolean;
  baseUrl?: string;
  ipoUrl?: string;
  isLoading?: boolean;
  isSeoApp?: boolean;
}

const IPODetails: FunctionComponent<Props> = ({
  queryParam,
  ipoDetails,
  statusTab,
  applyIpoButton,
  applicationDeleted = false,
  isLoading = false,
  baseUrl,
  isSeoApp = true,
}) => {
  const [tabId, setTabId] = useState(0);

  const screenWidth = useCurrentWidth();

  useEffect(() => {
    if (!statusTab && tabId === 2) {
      setTabId(0);
    }
  }, [queryParam, statusTab, tabId]);

  const getTabs = () => {
    const tabs = [
      {
        title: 'Overview',
        component: () => <OverViewTab ipoDetails={ipoDetails} />,
      },
      {
        title: 'Timeline',
        component: () => {
          return (
            <View css={css.ipoTimeline}>
              <IPOTimeLine
                biddingStartDate={ipoDetails?.biddingStartDate}
                biddingEndDate={ipoDetails?.biddingEndDate}
                allotmentDate={ipoDetails?.detail?.allotmentDate}
                refundInitiationDate={ipoDetails?.detail?.refundDate}
                dematTransferDate={ipoDetails?.detail?.creditDate}
                listingDate={ipoDetails?.detail?.listingDate}
              />
            </View>
          );
        },
      },
    ];
    if (screenWidth > 0 && screenWidth < 768 && statusTab) {
      tabs.push({
        title: 'Status',
        component: () => {
          return (
            <View
              flexDirection="column"
              margin={[16, 0, 0]}
              className="full-width"
            >
              {statusTab}
            </View>
          );
        },
      });
    }
    return tabs;
  };

  const headerStatus =
    isSeoApp &&
    ipoDetails &&
    [IPO_STATUS.active, IPO_STATUS.preApply, IPO_STATUS.closed].includes(
      ipoDetails.status,
    )
      ? ipoDetails.status === IPO_STATUS.preApply
        ? 'Pre-apply'
        : ipoDetails.status === IPO_STATUS.closed
        ? 'Closed'
        : 'Open now'
      : '';

  const headerTagColor =
    ipoDetails &&
    [IPO_STATUS.active, IPO_STATUS.preApply, IPO_STATUS.closed].includes(
      ipoDetails.status,
    )
      ? ipoDetails.status === IPO_STATUS.preApply
        ? 'yellow'
        : ipoDetails.status === IPO_STATUS.closed
        ? 'grey'
        : 'yellow'
      : 'grey';

  const getIpoPageContent = () => {
    if (isLoading) {
      return (
        <View
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          className="full-height"
        >
          <Loading className="mb20" color="text.2" />
        </View>
      );
    } else {
      return (
        <>
          <View flexDirection="column" css={css.details}>
            {isSeoApp ? (
              <>
                <View css={css.breadcrumb}>
                  <Breadcrumbs>
                    <Link href={process.env.BASE_URL}>
                      <a>Trading</a>
                    </Link>
                    <Link href={baseUrl || '/'}>
                      <a>IPO</a>
                    </Link>
                    <Text>{ipoDetails?.name}</Text>
                  </Breadcrumbs>
                </View>

                {ipoDetails && (
                  <>
                    <IPODetailsCard
                      isSeoApp={isSeoApp}
                      ipoDetails={ipoDetails}
                      headerTagColor={headerTagColor}
                      headerStatus={headerStatus}
                    />
                    <OverViewTab ipoDetails={ipoDetails} isSeoApp={isSeoApp} />
                  </>
                )}
              </>
            ) : (
              <>
                <IPOHeader
                  ipoIcon={ipoDetails?.logoUrl}
                  ipoName={ipoDetails?.name}
                  logoMeta={ipoDetails?.logoMeta}
                  ipoSymbol={ipoDetails?.symbol}
                  minPrice={ipoDetails?.minPrice}
                  maxPrice={ipoDetails?.maxPrice}
                  status={headerStatus}
                  tagColor={headerTagColor}
                />
                {ipoDetails && (
                  <TabBar
                    padding={[0, 15]}
                    margin={
                      !screenWidth && screenWidth < 768 ? [0, -15, 0] : [0]
                    }
                    id="ipoDetailsTabs"
                    tabs={getTabs()}
                    activeTab={
                      !screenWidth ||
                      (screenWidth < 768 && (applicationDeleted || !statusTab))
                        ? 0
                        : tabId
                    }
                    onTabSelect={(tabId: number) => setTabId(tabId)}
                  />
                )}
              </>
            )}
          </View>
          {screenWidth > 0 && screenWidth < 768 && tabId !== 2 && (
            <>
              {applyIpoButton && (
                <View flexDirection="column" css={css.applyWrapper}>
                  {applyIpoButton}
                </View>
              )}
            </>
          )}
        </>
      );
    }
  };

  return <>{getIpoPageContent()}</>;
};

export default observer(IPODetails);
